import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { ReactNode } from "react";

export interface KeyValueRow {
  label: string;
  value: ReactNode;
  /** Status chip shown beside the value. */
  chip?: ReactNode;
  /** Value tone: `success` for confirmations (design "Required", "NDPA signed"). */
  tone?: "default" | "success";
}

export interface KeyValueCardProps {
  title: string;
  rows: KeyValueRow[];
  action?: ReactNode;
}

/**
 * Titled card of label/value pairs (design "Sign-in & agreements"): 15 px uppercase heading,
 * then one row per pair with the label at the left and the muted value at the right. Rendered
 * as a description list.
 */
export const KeyValueCard = ({ title, rows, action }: KeyValueCardProps) => (
  <Paper
    component="section"
    sx={{
      p: "20px 22px",
      display: "flex",
      flexDirection: "column",
      gap: 1.75,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Typography variant="h6" component="h3">
        {title}
      </Typography>
      {action}
    </Box>
    <Box
      component="dl"
      sx={{ m: 0, display: "flex", flexDirection: "column", gap: 1.75 }}
    >
      {rows.map((row, index) => (
        <Box
          key={`${row.label}-${index}`}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            flexWrap: "wrap",
            gap: "4px 12px",
            fontSize: 13.5,
            fontWeight: 600,
          }}
        >
          <Typography
            component="dt"
            sx={{ fontSize: 13.5, fontWeight: 600, color: "text.primary" }}
          >
            {row.label}
          </Typography>
          <Box
            component="dd"
            sx={{
              m: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
              textAlign: "right",
              color: row.tone === "success" ? "successInk" : "text.secondary",
              overflowWrap: "anywhere",
            }}
          >
            {row.value}
            {row.chip}
          </Box>
        </Box>
      ))}
    </Box>
  </Paper>
);

export default KeyValueCard;
